import { useIntl, IntlShape } from 'react-intl';
import { useState, useEffect } from 'react';
import { Checkbox } from 'ui/Checkbox';
import { Radio } from 'ui/Radio';
import { RadioGroup } from 'ui/RadioGroup';
import { Label } from 'ui/Label';
import { InputNumber } from 'ui/InputNumber';
import { ListingProduct, FarmProduct } from '../../../../../types/ListingData';
import { getStorageUnitDisplay } from '../../../../../utils/storageUnitHelpers';
import { QualityMarkProductData } from 'types/ProductData';
import {
  QualityMarkOption,
  findQualityMark,
  hasRequiredQualityMark,
} from 'utils/qualityMarkHelpers';

interface ProductCardProps {
  product: {
    listing_product_id: string;
    offer_type: 'full_amount' | 'other_amount';
    farm_product_id?: number | null;
    offered_quantity?: number;
    offered_unit_id?: number;
    offered_price?: number;
    offered_price_unit_id?: number;
    listingProduct?: ListingProduct;
    id?: number;
    selected_quantity?: number | null;
  };
  index: number;
  farmProductNames: { [key: number]: string };
  availableProductsByCategory: { [categoryId: number]: FarmProduct[] };
  allQualityMarks?: QualityMarkOption[];
  selectionMode?: boolean;
  selectedProductsData?: Array<{
    listing_application_product_id: number;
    selected_quantity: number;
  }>;
  onSelectionChange?: (
    productId: string,
    selected: boolean,
    quantity: number,
    selectionType: 'full' | 'partial'
  ) => void;
  intl: IntlShape;
}

interface ProductsProps {
  displayProducts: Array<{
    listing_product_id: string;
    offer_type: 'full_amount' | 'other_amount';
    farm_product_id?: number | null;
    offered_quantity?: number;
    offered_unit_id?: number;
    offered_price?: number;
    offered_price_unit_id?: number;
    listingProduct?: ListingProduct;
    id?: number;
    selected_quantity?: number | null;
  }>;
  farmProductNames: { [key: number]: string };
  availableProductsByCategory?: { [categoryId: number]: FarmProduct[] };
  allQualityMarks?: QualityMarkOption[];
  selectionMode?: boolean;
  selectedProductsData?: Array<{
    listing_application_product_id: number;
    selected_quantity: number;
  }>;
  onSelectionChange?: (
    productId: string,
    selected: boolean,
    quantity: number,
    selectionType: 'full' | 'partial'
  ) => void;
}

const getStorageUnitShortById = (
  unitId: number | undefined,
  listingProduct?: ListingProduct
): string => {
  if (!unitId) return '';

  if (
    listingProduct?.storage_unit &&
    listingProduct.storage_unit.id === unitId &&
    listingProduct.storage_unit
  ) {
    return getStorageUnitDisplay({
      id: listingProduct.storage_unit.id,
      name: listingProduct.storage_unit.name || '',
      short: listingProduct.storage_unit.short,
    });
  }

  return '';
};

const ProductCard = ({
  product,
  index,
  farmProductNames,
  availableProductsByCategory,
  allQualityMarks,
  selectionMode = false,
  selectedProductsData = [],
  onSelectionChange,
  intl,
}: ProductCardProps) => {
  const listingProduct = product.listingProduct;

  const selectedProductName =
    product.farm_product_id && farmProductNames[product.farm_product_id]
      ? farmProductNames[product.farm_product_id]
      : listingProduct?.name || listingProduct?.category?.name || '';

  const selectedProduct =
    product.farm_product_id && listingProduct?.category_id
      ? availableProductsByCategory[listingProduct.category_id]?.find(
          (p) => p.id === product.farm_product_id
        )
      : undefined;

  const selectedProductQualityMarkIds = selectedProduct?.qualitymarks
    ? selectedProduct.qualitymarks.map(
        (qm: QualityMarkProductData) => qm.quality_mark_id
      )
    : [];

  const offeredQuantity =
    product.offered_quantity || product.listingProduct?.required_quantity || 0;
  const existingSelection = selectedProductsData?.find(
    (sel) => sel.listing_application_product_id === product.id
  );
  const hasExistingSelection = !!existingSelection;
  const acceptedQuantity = product.selected_quantity ?? null;
  const displayQuantity =
    existingSelection?.selected_quantity ??
    acceptedQuantity ??
    (selectionMode
      ? (product.offered_quantity ?? listingProduct?.required_quantity ?? '')
      : 0);
  const contentQuantity =
    existingSelection?.selected_quantity ??
    acceptedQuantity ??
    product.offered_quantity ??
    '';
  const isFullQuantity =
    existingSelection &&
    existingSelection.selected_quantity === offeredQuantity;

  const [selectionType, setSelectionType] = useState<
    'full' | 'partial' | undefined
  >(hasExistingSelection ? (isFullQuantity ? 'full' : 'partial') : undefined);
  const [customQuantity, setCustomQuantity] = useState<number | undefined>(
    existingSelection && !isFullQuantity
      ? existingSelection.selected_quantity
      : undefined
  );

  useEffect(() => {
    if (!selectionMode || !selectionType) return;

    if (
      selectionType === 'partial' &&
      (customQuantity === undefined || customQuantity <= 0)
    ) {
      onSelectionChange?.(product.listing_product_id, false, 0, selectionType);
      return;
    }

    onSelectionChange?.(
      product.listing_product_id,
      true,
      selectionType === 'full' ? offeredQuantity : (customQuantity as number),
      selectionType
    );
  }, [selectionType, customQuantity]);

  return (
    <div key={product.listing_product_id} className="product-card">
      <div className="product-header">
        <Label label={index + 1} className="product-number" />
        <div className="product-title">
          <Label
            title={true}
            label={`${selectedProductName} (${intl.formatMessage({ id: 'listings.application.quantity' })}: ${displayQuantity} ${getStorageUnitDisplay(listingProduct?.storage_unit)}${listingProduct?.min_acceptable_offer_quantity ? `, ${intl.formatMessage({ id: 'listings.application.min_quantity' })}: ${listingProduct.min_acceptable_offer_quantity} ${getStorageUnitDisplay(listingProduct?.storage_unit)}` : ''})`}
          />

          {listingProduct?.quality_mark_ids &&
            listingProduct.quality_mark_ids.length > 0 && (
              <div className="quality-marks">
                {listingProduct.quality_mark_ids.map((id) => {
                  const qmark = findQualityMark(allQualityMarks, id);
                  const hasProductQualityMark = hasRequiredQualityMark(
                    id,
                    selectedProductQualityMarkIds,
                    allQualityMarks
                  );
                  return (
                    <div
                      key={id}
                      className={`quality-mark-item ${
                        hasProductQualityMark
                          ? 'has-quality-mark'
                          : 'no-quality-mark'
                      }`}
                    >
                      <Checkbox checked={true} disabled />
                      <Label label={qmark?.name || ''} />
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      </div>

      <div className="product-content">
        <Label
          label={intl.formatMessage({
            id:
              product.offer_type === 'full_amount'
                ? 'listings.application.can_offer_full_amount'
                : 'listings.application.other_amounts',
          })}
          bold
        />
        {product.offer_type === 'full_amount' ? (
          product.offered_price !== undefined &&
          product.offered_price !== null &&
          String(product.offered_price) !== '' &&
          Number(product.offered_price) > 0 && (
            <Label
              label={`${intl.formatMessage({ id: 'listings.application.price' })}: ${product.offered_price} EUR/${getStorageUnitShortById(product.offered_price_unit_id, listingProduct)}`}
            />
          )
        ) : (
          <>
            <Label
              label={`${intl.formatMessage({ id: 'listings.application.quantity' })}: ${contentQuantity} ${getStorageUnitShortById(product.offered_unit_id, listingProduct)}`}
            />
            {product.offered_price !== undefined &&
              product.offered_price !== null &&
              String(product.offered_price) !== '' &&
              Number(product.offered_price) > 0 && (
                <Label
                  label={`${intl.formatMessage({ id: 'listings.application.price' })}: ${product.offered_price} EUR/${getStorageUnitShortById(product.offered_price_unit_id, listingProduct)}`}
                />
              )}
          </>
        )}
      </div>

      {selectionMode && (
        <div className="selection-controls">
          <RadioGroup
            value={selectionType}
            onChange={(e) =>
              setSelectionType(e.target.value as 'full' | 'partial' | undefined)
            }
            className="selection-radio-group"
          >
            <Radio
              value="full"
              label={intl.formatMessage({
                id: 'listings.application.select_all',
              })}
            />
            <Radio
              value="partial"
              label={intl.formatMessage({
                id: 'listings.application.select_partial',
              })}
            />
          </RadioGroup>

          {selectionType === 'partial' && (
            <InputNumber
              label={intl.formatMessage({
                id: 'listings.application.quantity',
              })}
              value={customQuantity}
              onChange={(value) => {
                if (value === null || value === undefined || value === '') {
                  setCustomQuantity(undefined);
                  return;
                }
                const parsed =
                  typeof value === 'number'
                    ? value
                    : parseFloat(value as string);
                setCustomQuantity(Number.isNaN(parsed) ? undefined : parsed);
              }}
              min={0.01}
              max={offeredQuantity}
              step={0.01}
              precision={2}
            />
          )}
        </div>
      )}
    </div>
  );
};

export const Products = ({
  displayProducts,
  farmProductNames,
  availableProductsByCategory = {},
  allQualityMarks,
  selectionMode = false,
  selectedProductsData = [],
  onSelectionChange,
}: ProductsProps) => {
  const intl = useIntl();

  return (
    <div className="summary-section">
      <Label
        label={intl.formatMessage({
          id: 'listings.application.products',
        })}
        title={true}
      />
      <div className="products-list">
        {displayProducts.map((product, index) => (
          <ProductCard
            key={product.listing_product_id}
            product={product}
            index={index}
            farmProductNames={farmProductNames}
            availableProductsByCategory={availableProductsByCategory}
            allQualityMarks={allQualityMarks}
            selectionMode={selectionMode}
            selectedProductsData={selectedProductsData}
            onSelectionChange={onSelectionChange}
            intl={intl}
          />
        ))}
      </div>
    </div>
  );
};
